import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { loadInitialData, clearCache, getInitialCachedDirectoryEntries } from './api/client';
import { useAuth, useGoogleButton } from './contexts/AuthContext';
import { SkeletonPage } from './components/SkeletonLoader';
import { EmailComboboxInput } from './components/EmailComboboxInput';
import type { DirectoryEntry, Facets, Branding } from './types/api';
import type { UserRole } from './types/roles';
import {
  isCategoryAllowedForRoles,
  extractAllUserProfilesFromEntries,
  getUserDisplayName,
  resolveUserRoleProfile,
  hasUserAccessToEntry,
  hasUserAccessToDailyTask,
} from './utils/roleUtils';
import { AUTHORIZED_ADMIN_EMAILS } from './config/rolePermissions';
import { APP_VERSION, APP_BUILD_DATE } from './config/version';
import { ensureActiveSheetTabs, getFormattedEntryTitle, groupEntriesBySpreadsheetForSearch } from './utils/tabUtils';
import { evaluateSearchMatch, matchesSearch } from './utils/searchUtils';
import {
  syncAdminSettingsWithServer,
  onAdminSettingsChange,
  getLocalAdminSettings,
  saveTeamSheetOrder,
  resetTeamSheetOrder,
  savePrintableDocGroup,
  deletePrintableDocGroup,
  isGroupVisibleToUser,
  type GlobalAdminSettings,
  type PrintableDocGroup,
} from './utils/adminSync';
import { RaiseHandModal } from './components/RaiseHandModal';
import { SupportRequestsTrackerModal } from './components/SupportRequestsTrackerModal';
import { AddToGroupModal } from './components/AddToGroupModal';
import { FileInfoTooltip } from './components/FileInfoTooltip';
import { getDailyTasksList, syncDailyTasksFromSource } from './utils/dailyTasksSource';
import type { SupportTargetFile, SupportRequest } from './types/support';
import {
  getLocalSupportRequests,
  onSupportRequestsChange,
  syncSupportRequestsWithServer,
} from './utils/supportSync';
import { logFileOpen } from './utils/usageLogger';
import printableDocsData from './data/printableDocuments.json';

interface PrintableDoc {
  id: string;
  docNumber: string;
  category: string;
  title: string;
  fullName: string;
  pdfUrl: string;
  docUrl: string;
  updateDate: string;
}

const LEGACY_DOC_ID_ALIASES: Record<string, string> = {
  'print-doc-94': 'print-doc-128',
  'print-doc-95': 'print-doc-127',
  'print-doc-96': 'print-doc-126',
  'print-doc-97': 'print-doc-125',
  'print-doc-99': 'print-doc-124',
};

interface TeamOption {
  name: string;
  count: number;
  isAssigned?: boolean;
}

interface MultiSelectTeamDropdownProps {
  selectedTeams: string[];
  onChange: (teams: string[]) => void;
  teams: TeamOption[];
  userAssignedTeams?: string[];
  totalSheetsCount: number;
  colorScheme?: 'purple' | 'blue' | 'emerald' | 'green';
}

function MultiSelectTeamDropdown({
  selectedTeams,
  onChange,
  teams,
  userAssignedTeams = [],
  totalSheetsCount,
  colorScheme = 'purple',
}: MultiSelectTeamDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [teamSearch, setTeamSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const isAllSelected = selectedTeams.length === 0 || selectedTeams.includes('All');

  const handleToggleTeam = (teamName: string) => {
    if (teamName === 'All') {
      onChange(['All']);
      return;
    }

    let current = selectedTeams.filter((t) => t !== 'All');
    if (current.includes(teamName)) {
      current = current.filter((t) => t !== teamName);
    } else {
      current = [...current, teamName];
    }

    if (current.length === 0) {
      onChange(['All']);
    } else {
      onChange(current);
    }
  };

  const handleSelectAll = () => {
    onChange(['All']);
  };

  const handleSelectMyTeams = () => {
    if (userAssignedTeams.length > 0) {
      onChange(userAssignedTeams);
    }
  };

  const handleClearAll = () => {
    onChange(['All']);
  };

  // Filtered teams list based on internal search
  const filteredTeams = useMemo(() => {
    if (!teamSearch.trim()) return teams;
    return teams.filter((t) => matchesSearch(teamSearch, { primary: t.name }));
  }, [teams, teamSearch]);

  const assignedTeamsList = useMemo(() => filteredTeams.filter((t) => t.isAssigned), [filteredTeams]);
  const otherTeamsList = useMemo(() => filteredTeams.filter((t) => !t.isAssigned), [filteredTeams]);

  // Button label text
  const buttonLabel = useMemo(() => {
    if (isAllSelected) {
      return '🌐 All Teams (' + totalSheetsCount + ')';
    }
    if (selectedTeams.length === 1) {
      const match = teams.find((t) => t.name === selectedTeams[0]);
      return '👥 ' + selectedTeams[0] + ' (' + (match?.count ?? 0) + ')';
    }
    if (selectedTeams.length === 2) {
      return '👥 ' + selectedTeams[0] + ', ' + selectedTeams[1];
    }
    return '👥 ' + selectedTeams.length + ' Teams Selected';
  }, [isAllSelected, selectedTeams, teams, totalSheetsCount]);

  const isEmerald = colorScheme === 'emerald' || colorScheme === 'green';
  const isPurple = colorScheme === 'purple';

  const borderFocusClass = isEmerald
    ? 'border-emerald-300 dark:border-emerald-700/60 focus:ring-emerald-500'
    : isPurple
    ? 'border-purple-300 dark:border-purple-700/60 focus:ring-purple-500'
    : 'border-blue-300 dark:border-blue-700/60 focus:ring-blue-500';

  const accentBadgeClass = isEmerald
    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
    : isPurple
    ? 'bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300'
    : 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300';

  const accentSelectedRowClass = isEmerald
    ? 'bg-emerald-50/80 dark:bg-emerald-950/40 text-emerald-900 dark:text-emerald-200'
    : isPurple
    ? 'bg-purple-50/80 dark:bg-purple-950/40 text-purple-900 dark:text-purple-200'
    : 'bg-blue-50/80 dark:bg-blue-950/40 text-blue-900 dark:text-blue-200';

  const accentFocusRing = isEmerald
    ? 'focus:ring-emerald-500'
    : isPurple
    ? 'focus:ring-purple-500'
    : 'focus:ring-blue-500';

  const accentActiveTextClass = isEmerald
    ? 'text-emerald-600 dark:text-emerald-400 font-bold'
    : isPurple
    ? 'text-purple-600 dark:text-purple-400 font-bold'
    : 'text-blue-600 dark:text-blue-400 font-bold';

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {/* Trigger Button */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={'w-full text-xs bg-white dark:bg-gray-800 border ' + borderFocusClass + ' rounded-lg px-2.5 py-1.5 text-gray-900 dark:text-gray-100 font-semibold shadow-sm cursor-pointer flex items-center justify-between gap-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors select-none'}
      >
        <span className="truncate">{buttonLabel}</span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {!isAllSelected && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                handleClearAll();
              }}
              title="Reset to All Teams"
              className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-xs leading-none"
            >
              ✕
            </span>
          )}
          <svg
            className={'w-3.5 h-3.5 text-gray-500 transition-transform duration-150 ' + (isOpen ? 'transform rotate-180' : '')}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Dropdown Popover */}
      {isOpen && (
        <div className="absolute left-0 top-full mt-1 w-full min-w-[260px] sm:w-72 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden flex flex-col max-h-80 animate-in fade-in zoom-in-95 duration-100">
          {/* Internal Search */}
          <div className="p-2 border-b border-gray-100 dark:border-gray-700/80 bg-gray-50 dark:bg-gray-900/60">
            <input
              type="text"
              placeholder="Filter teams..."
              value={teamSearch}
              onChange={(e) => setTeamSearch(e.target.value)}
              className={'w-full text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-2.5 py-1 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-1 ' + accentFocusRing}
              autoFocus
            />
            {/* Quick Action Buttons */}
            <div className="flex items-center justify-between gap-1 mt-1.5 pt-1 text-[10px]">
              <button
                type="button"
                onClick={handleSelectAll}
                className={'font-semibold px-1.5 py-0.5 rounded hover:underline ' + (isAllSelected ? accentActiveTextClass : 'text-gray-600 dark:text-gray-400')}
              >
                Select All
              </button>
              {userAssignedTeams.length > 0 && (
                <button
                  type="button"
                  onClick={handleSelectMyTeams}
                  className="font-semibold px-1.5 py-0.5 rounded text-amber-600 dark:text-amber-400 hover:underline"
                >
                  My Teams
                </button>
              )}
              <button
                type="button"
                onClick={handleClearAll}
                className="font-medium px-1.5 py-0.5 rounded text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 hover:underline"
              >
                Reset
              </button>
            </div>
          </div>

          {/* Teams List */}
          <div className="flex-1 overflow-y-auto p-1.5 space-y-0.5 divide-y divide-gray-100 dark:divide-gray-800/40">
            {/* Option: All Teams */}
            <div
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleToggleTeam('All');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleToggleTeam('All');
                }
              }}
              className={'flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs cursor-pointer select-none transition-colors ' + (isAllSelected ? accentSelectedRowClass : 'hover:bg-gray-100 dark:hover:bg-gray-700/60 text-gray-800 dark:text-gray-200')}
            >
              <div className="flex items-center gap-2 min-w-0">
                <div
                  className={'w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors flex-shrink-0 ' + (
                    isAllSelected
                      ? isEmerald
                        ? 'bg-emerald-600 border-emerald-600 text-white'
                        : isPurple
                        ? 'bg-purple-600 border-purple-600 text-white'
                        : 'bg-blue-600 border-blue-600 text-white'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
                  )}
                >
                  {isAllSelected && (
                    <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <span className="font-semibold truncate">🌐 All Teams</span>
              </div>
              <span className={'text-[10px] font-bold px-1.5 py-0.2 rounded-full ' + accentBadgeClass}>
                {totalSheetsCount}
              </span>
            </div>

            {/* Group: Assigned Teams */}
            {assignedTeamsList.length > 0 && (
              <div className="pt-1.5">
                <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400">
                  Your Assigned Teams
                </div>
                {assignedTeamsList.map((team) => {
                  const isChecked = !isAllSelected && selectedTeams.includes(team.name);
                  return (
                    <div
                      key={'assigned-' + team.name}
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleToggleTeam(team.name);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleToggleTeam(team.name);
                        }
                      }}
                      className={'flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs cursor-pointer select-none transition-colors ' + (isChecked ? accentSelectedRowClass : 'hover:bg-gray-100 dark:hover:bg-gray-700/60 text-gray-800 dark:text-gray-200')}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <div
                          className={'w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors flex-shrink-0 ' + (
                            isChecked
                              ? isEmerald
                                ? 'bg-emerald-600 border-emerald-600 text-white'
                                : isPurple
                                ? 'bg-purple-600 border-purple-600 text-white'
                                : 'bg-blue-600 border-blue-600 text-white'
                              : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
                          )}
                        >
                          {isChecked && (
                            <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <span className="truncate font-medium">{team.name}</span>
                      </div>
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 font-semibold px-1.5 py-0.2">
                        {team.count}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Group: Other Teams */}
            {otherTeamsList.length > 0 && (
              <div className="pt-1.5">
                {assignedTeamsList.length > 0 && (
                  <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                    👥 Other Teams
                  </div>
                )}
                {otherTeamsList.map((team) => {
                  const isChecked = !isAllSelected && selectedTeams.includes(team.name);
                  return (
                    <div
                      key={'other-' + team.name}
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleToggleTeam(team.name);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleToggleTeam(team.name);
                        }
                      }}
                      className={'flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs cursor-pointer select-none transition-colors ' + (isChecked ? accentSelectedRowClass : 'hover:bg-gray-100 dark:hover:bg-gray-700/60 text-gray-800 dark:text-gray-200')}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <div
                          className={'w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors flex-shrink-0 ' + (
                            isChecked
                              ? isEmerald
                                ? 'bg-emerald-600 border-emerald-600 text-white'
                                : isPurple
                                ? 'bg-purple-600 border-purple-600 text-white'
                                : 'bg-blue-600 border-blue-600 text-white'
                              : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
                          )}
                        >
                          {isChecked && (
                            <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <span className="truncate font-medium">👥 {team.name}</span>
                      </div>
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 font-semibold px-1.5 py-0.2">
                        {team.count}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {filteredTeams.length === 0 && (
              <div className="p-3 text-center text-xs text-gray-400">
                No teams match "{teamSearch}"
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function App() {
  const auth = useAuth();
  const { renderButton } = useGoogleButton();
  const googleButtonRef = useRef<HTMLDivElement>(null);

  const [entries, setEntries] = useState<DirectoryEntry[]>(() => getInitialCachedDirectoryEntries());
  const [, setFacets] = useState<Facets | null>(null);
  const [branding, setBranding] = useState<Branding | null>(null);
  const [loading, setLoading] = useState<boolean>(() => false);
  const [error, setError] = useState<string | null>(null);

  // Dark mode state
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('ssd-dark-mode');
      return saved ? JSON.parse(saved) : true;
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('ssd-dark-mode', JSON.stringify(darkMode));
    } catch {
      // ignore
    }
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Column 1 state (Daily Tasks)
  const [col1SelectedTeams, setCol1SelectedTeams] = useState<string[]>(['All']);
  const [col1Search, setCol1Search] = useState('');

  // Column 2 state (Printable Documents Hub & Groups)
  const [col2SelectedGroup, setCol2SelectedGroup] = useState<string>('All');
  const [col2Search, setCol2Search] = useState('');
  const [previewDoc, setPreviewDoc] = useState<PrintableDoc | null>(null);
  const [docForGroupModal, setDocForGroupModal] = useState<PrintableDoc | null>(null);
  const [editingGroupIdForModal, setEditingGroupIdForModal] = useState<string | null>(null);
  const [showGroupModal, setShowGroupModal] = useState(false);

  // Column 3 state (All Sheets)
  const [col3SelectedTeams, setCol3SelectedTeams] = useState<string[]>(['All']);
  const [col3Search, setCol3Search] = useState('');

  // Support Requests ("Raise Hand" ✋) State
  const [selectedFileForSupport, setSelectedFileForSupport] = useState<SupportTargetFile | null>(null);
  const [showSupportTrackerModal, setShowSupportTrackerModal] = useState(false);
  const [allSupportRequests, setAllSupportRequests] = useState<SupportRequest[]>(() => getLocalSupportRequests());

  // Real-time synchronization of support requests
  useEffect(() => {
    if (!auth.isAuthenticated) return;
    syncSupportRequestsWithServer().then(() => {
      setAllSupportRequests(getLocalSupportRequests());
    });
    const unsub = onSupportRequestsChange((reqs) => {
      setAllSupportRequests(reqs);
    });
    return unsub;
  }, [auth.isAuthenticated]);

  // Count active/pending support requests for current user or their assignments
  const pendingSupportCount = useMemo(() => {
    const email = (auth.previewUserEmail || auth.user?.email || '').toLowerCase().trim();
    if (!email) return 0;
    return allSupportRequests.filter(
      (r) =>
        (r.userEmail.toLowerCase() === email ||
          (r.assignedEmail && r.assignedEmail.toLowerCase() === email) ||
          (email.includes('admin') && (r.assignedEmail?.toLowerCase().includes('admin') || true))) &&
        (r.status === 'Pending' || r.status === 'In Progress')
    ).length;
  }, [allSupportRequests, auth.previewUserEmail, auth.user?.email]);

  // SuperAdmin Preview Modal State
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewModalTab, setPreviewModalTab] = useState<'user' | 'team'>('user');
  const [previewUserSearch, setPreviewUserSearch] = useState('');
  const [selectedPreviewEmail, setSelectedPreviewEmail] = useState('frontdesk@ssdspc.com');
  const [selectedPreviewTeam, setSelectedPreviewTeam] = useState('Front Desk');
  const [customPreviewEmail, setCustomPreviewEmail] = useState('');

  // Pinned items (persisted in localStorage per user)
  const activeUserEmail = useMemo(() => {
    return (auth.previewUserEmail || auth.user?.email || '').toLowerCase().trim();
  }, [auth.previewUserEmail, auth.user?.email]);

  const [pinnedIds, setPinnedIds] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('ssd-intranet-pinned-sheets');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Keep pinned state synced per user
  useEffect(() => {
    if (!activeUserEmail) return;
    try {
      const perUserKey = 'ssd-pinned-' + activeUserEmail;
      const stored = localStorage.getItem(perUserKey) || localStorage.getItem('ssd-intranet-pinned-sheets');
      if (stored) {
        setPinnedIds(JSON.parse(stored));
      }
    } catch {
      // ignore
    }
  }, [activeUserEmail]);

  const togglePin = (id: string, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setPinnedIds((prev) => {
      const next = prev.includes(id) ? prev.filter((p) => p !== id) : [id, ...prev];
      try {
        if (activeUserEmail) {
          localStorage.setItem('ssd-pinned-' + activeUserEmail, JSON.stringify(next));
        }
        localStorage.setItem('ssd-intranet-pinned-sheets', JSON.stringify(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  // Google Login button rendering
  useEffect(() => {
    if (!auth.isAuthenticated && !auth.isLoading && googleButtonRef.current) {
      googleButtonRef.current.innerHTML = '';
      renderButton(googleButtonRef.current);
    }
  }, [auth.isAuthenticated, auth.isLoading, renderButton]);

  // Load initial data (stale-while-revalidate background refresh)
  useEffect(() => {
    async function init() {
      try {
        const data = await loadInitialData();
        if (data.directory.entries && data.directory.entries.length > 0) {
          const verifiedEntries = ensureActiveSheetTabs(data.directory.entries);
          setEntries(verifiedEntries);
        }
        if (data.facets.success && data.facets.facets) {
          setFacets(data.facets.facets);
        }
        if (data.branding.success && data.branding.branding) {
          setBranding(data.branding.branding);
        }
        // Background sync daily tasks master sheet
        syncDailyTasksFromSource().catch(() => {});
        setError(null);
      } catch (err) {
        // Only set error if we have no entries at all
        setEntries((current) => {
          if (current.length === 0) {
            setError(err instanceof Error ? err.message : 'Failed to load intranet data');
          }
          return current;
        });
      } finally {
        setLoading(false);
      }
    }
    init();
  }, []);

  // Real-time composite admin settings & team sheet orders state
  const [adminSettingsState, setAdminSettingsState] = useState<GlobalAdminSettings>(() => getLocalAdminSettings());

  // Reorder mode states for Column 1 & Column 3
  const [col1Reordering, setCol1Reordering] = useState(false);
  const [col3Reordering, setCol3Reordering] = useState(false);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [reorderSavedNotice, setReorderSavedNotice] = useState<string | null>(null);
  const [teamPickerCol, setTeamPickerCol] = useState<1 | 3 | null>(null);

  // Real-time synchronization of admin settings across users & devices
  useEffect(() => {
    if (!auth.isAuthenticated) return;
    const interval = setInterval(() => {
      syncAdminSettingsWithServer().then((updated) => {
        if (updated) {
          setAdminSettingsState(getLocalAdminSettings());
          setEntries((prev) => ensureActiveSheetTabs([...prev]));
        }
      });
    }, 15000);

    const unsubscribe = onAdminSettingsChange((settings) => {
      setAdminSettingsState(settings);
      setEntries((prev) => ensureActiveSheetTabs([...prev]));
    });

    return () => {
      clearInterval(interval);
      unsubscribe();
    };
  }, [auth.isAuthenticated]);

  // Handler to move sheet up or down for a specific team (SuperAdmin / Admin only)
  const handleMoveSheet = async (column: 1 | 3, targetTeam: string, currentIndex: number, direction: 'up' | 'down') => {
    if (!isAdminUser || !targetTeam) return;
    const list = column === 1 ? col1Entries : col3Entries;
    const destIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (destIndex < 0 || destIndex >= list.length) return;

    const newList = [...list];
    const itemA = newList[currentIndex];
    const itemB = newList[destIndex];
    newList[currentIndex] = itemB;
    newList[destIndex] = itemA;

    const orderedIds = newList.map((item) => item.id);
    await saveTeamSheetOrder(targetTeam, orderedIds);
    setAdminSettingsState(getLocalAdminSettings());

    setReorderSavedNotice(`Saved order for ${targetTeam}`);
    setTimeout(() => setReorderSavedNotice(null), 3000);
  };

  // Handler for HTML5 drag-and-drop reordering (SuperAdmin / Admin only)
  const handleDropSheet = async (column: 1 | 3, targetTeam: string, sourceId: string, targetId: string) => {
    if (!isAdminUser || !targetTeam || !sourceId || !targetId || sourceId === targetId) return;
    const list = column === 1 ? col1Entries : col3Entries;
    const fromIndex = list.findIndex((item) => item.id === sourceId);
    const toIndex = list.findIndex((item) => item.id === targetId);
    if (fromIndex === -1 || toIndex === -1) return;

    const newList = [...list];
    const [moved] = newList.splice(fromIndex, 1);
    newList.splice(toIndex, 0, moved);

    const orderedIds = newList.map((item) => item.id);
    setDraggedId(null);
    setDragOverId(null);

    await saveTeamSheetOrder(targetTeam, orderedIds);
    setAdminSettingsState(getLocalAdminSettings());

    setReorderSavedNotice(`Saved order for ${targetTeam}`);
    setTimeout(() => setReorderSavedNotice(null), 3000);
  };

  // Handler to reset team sheet order back to default (SuperAdmin / Admin only)
  const handleResetTeamOrder = async (targetTeam: string) => {
    if (!isAdminUser || !targetTeam) return;
    await resetTeamSheetOrder(targetTeam);
    setAdminSettingsState(getLocalAdminSettings());
    setReorderSavedNotice(`Reset order to default for ${targetTeam}`);
    setTimeout(() => setReorderSavedNotice(null), 3000);
  };

  // Real-time automatic app version upgrade detector (polls public/version.json every 10s)
  useEffect(() => {
    const checkVersion = async () => {
      try {
        const res = await fetch('/version.json?t=' + Date.now());
        if (res.ok) {
          const data = await res.json();
          if (data.version && data.version !== APP_VERSION) {
            console.log('[AutoUpdate] New version deployed (' + data.version + ' vs current ' + APP_VERSION + '). Reloading page...');
            window.location.reload();
          }
        }
      } catch (e) {
        console.warn('[AutoUpdate] Could not check version.json', e);
      }
    };

    const timer = setInterval(checkVersion, 10000);
    return () => clearInterval(timer);
  }, []);

  // Dynamically extract user profiles directly from entries & admin mappings
  const allSheetUserProfiles = useMemo(() => {
    const { userMappings } = getLocalAdminSettings();
    return extractAllUserProfilesFromEntries(entries, userMappings || {});
  }, [entries]);

  const activeSheetUserProfile = useMemo(() => {
    if (!activeUserEmail) return null;
    return allSheetUserProfiles.find((p) => p.email === activeUserEmail) || null;
  }, [activeUserEmail, allSheetUserProfiles]);

  const userRoleProfile = useMemo(() => {
    if (!activeUserEmail) return null;
    return resolveUserRoleProfile(activeUserEmail);
  }, [activeUserEmail]);

  const userRoles: UserRole[] = useMemo(() => {
    if (activeSheetUserProfile?.roles && activeSheetUserProfile.roles.length > 0) {
      return activeSheetUserProfile.roles;
    }
    if (userRoleProfile?.roles && userRoleProfile.roles.length > 0) {
      return userRoleProfile.roles;
    }
    return (auth.user?.roles || ['Staff']) as UserRole[];
  }, [auth.user?.roles, activeSheetUserProfile, userRoleProfile]);

  const userAssignedTeams = useMemo<string[]>(() => {
    if (!activeUserEmail) return [];

    const authAssigned = (auth.user as any)?.assignedTeams as string[] | undefined;
    if (authAssigned && Array.isArray(authAssigned) && authAssigned.length > 0) {
      const valid = authAssigned.filter((t: string) => t && t.toLowerCase() !== 'no team' && t.toLowerCase() !== 'management' && t.toLowerCase() !== 'unassigned');
      if (valid.length > 0) return valid;
    }

    if (activeSheetUserProfile?.assignedTeams && activeSheetUserProfile.assignedTeams.length > 0) {
      const valid = activeSheetUserProfile.assignedTeams.filter((t: string) => t && t.toLowerCase() !== 'no team' && t.toLowerCase() !== 'management' && t.toLowerCase() !== 'unassigned');
      if (valid.length > 0) return valid;
    }

    if (userRoleProfile?.assignedTeams && userRoleProfile.assignedTeams.length > 0) {
      const valid = userRoleProfile.assignedTeams.filter((t: string) => t && t.toLowerCase() !== 'no team' && t.toLowerCase() !== 'management' && t.toLowerCase() !== 'unassigned');
      if (valid.length > 0) return valid;
    }

    const primary = auth.user?.primaryTeam || activeSheetUserProfile?.primaryTeam || userRoleProfile?.primaryTeam || '';
    if (primary && primary.toLowerCase() !== 'no team' && primary.toLowerCase() !== 'management' && primary.toLowerCase() !== 'unassigned') {
      return [primary];
    }

    return [];
  }, [activeUserEmail, auth.user, activeSheetUserProfile, userRoleProfile]);

  const isAdminUser = useMemo(() => {
    return AUTHORIZED_ADMIN_EMAILS.some((e) => e.toLowerCase() === activeUserEmail) || userRoles.includes('Admin');
  }, [activeUserEmail, userRoles]);

  const lastInitializedEmailRef = useRef<string | null>(null);

  // Set default selected teams once on initial load or when switching preview user
  useEffect(() => {
    if (!activeUserEmail) return;
    if (lastInitializedEmailRef.current !== activeUserEmail) {
      if (userAssignedTeams.length > 0) {
        lastInitializedEmailRef.current = activeUserEmail;
        setCol1SelectedTeams(userAssignedTeams);
        setCol3SelectedTeams(userAssignedTeams);
      } else if (!loading) {
        lastInitializedEmailRef.current = activeUserEmail;
        setCol1SelectedTeams(['All']);
        setCol3SelectedTeams(['All']);
      }
    }
  }, [activeUserEmail, userAssignedTeams, loading]);

  // Real-time team sheet counts (filtered by user access permissions)
  const teamSheetCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    entries.forEach((entry) => {
      if (!entry) return;
      // Strictly check if active user has permission to view this sheet
      if (!hasUserAccessToEntry(entry, activeUserEmail, userRoles, userAssignedTeams)) {
        return;
      }
      if (entry.team) {
        entry.team
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean)
          .forEach((t) => {
            const key = t.toLowerCase();
            counts[key] = (counts[key] || 0) + 1;
          });
      }
    });
    return counts;
  }, [entries, activeUserEmail, userRoles, userAssignedTeams]);

  // Sorted list of all valid teams with assigned teams first
  const sortedTeamsList = useMemo(() => {
    const adminSettings = getLocalAdminSettings();
    const deletedTeams = adminSettings.deletedTeams || [];
    const deletedSet = new Set(deletedTeams.map((t) => t.toLowerCase().trim()));

    const userAssignedLowerSet = new Set(
      userAssignedTeams.map((t: string) => t.toLowerCase().trim())
    );

    const allTeamNamesSet = new Set<string>();
    (adminSettings.customTeams || []).forEach((t) => {
      if (t) allTeamNamesSet.add(t.trim());
    });
    (adminSettings.sheetCustomTeams || []).forEach((t) => {
      if (t) allTeamNamesSet.add(t.trim());
    });
    entries.forEach((e) => {
      if (e && typeof e.team === 'string') {
        if (hasUserAccessToEntry(e, activeUserEmail, userRoles, userAssignedTeams)) {
          e.team
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean)
            .forEach((t) => allTeamNamesSet.add(t));
        }
      }
    });
    userAssignedTeams.forEach((t: string) => {
      if (t) allTeamNamesSet.add(t.trim());
    });
    ['Front Desk', 'Billing Team', 'Clinical', 'Operations', 'Admin Team', "Dr. Mario's Team", 'Lab Team'].forEach((t) =>
      allTeamNamesSet.add(t)
    );
    // Only include SuperAdmin in the team filter list if user is an admin or has accessible sheets under SuperAdmin
    if (isAdminUser || (teamSheetCounts['superadmin'] || 0) > 0) {
      allTeamNamesSet.add('SuperAdmin');
    }

    const validTeamNames = Array.from(allTeamNamesSet).filter((t) => {
      const lower = t.toLowerCase().trim();
      return (
        lower !== '' &&
        lower !== 'management' &&
        lower !== 'no team' &&
        lower !== 'unassigned' &&
        !deletedSet.has(lower)
      );
    });

    return validTeamNames
      .map((name) => {
        const count = teamSheetCounts[name.toLowerCase().trim()] || 0;
        const isAssigned = userAssignedLowerSet.has(name.toLowerCase().trim());
        return { name, count, isAssigned };
      })
      .sort((a, b) => {
        if (a.isAssigned && !b.isAssigned) return -1;
        if (!a.isAssigned && b.isAssigned) return 1;
        return a.name.localeCompare(b.name);
      });
  }, [entries, userAssignedTeams, teamSheetCounts, activeUserEmail, userRoles, isAdminUser]);

  // Modal team list for SuperAdmin preview
  const allAvailableTeamsForModal = useMemo(() => {
    const set = new Set<string>();
    sortedTeamsList.forEach(t => set.add(t.name));
    return Array.from(set).sort();
  }, [sortedTeamsList]);

  // Total accessible sheets for Column 3
  const totalAccessibleCol3SheetsCount = useMemo(() => {
    return entries.filter((e) => hasUserAccessToEntry(e, activeUserEmail, userRoles, userAssignedTeams)).length;
  }, [entries, activeUserEmail, userRoles, userAssignedTeams]);

  // =========================================================================
  // COLUMN 1: Daily Tasks (Workflow Priority & Google Sheet Master Source)
  // =========================================================================

  // 1. All Daily Tasks accessible to current user (regardless of team dropdown or search filters)
  const allAccessibleDailyTasks = useMemo(() => {
    const pinnedSet = new Set(pinnedIds);
    const dailySource = getDailyTasksList();

    // Map each daily item to ensure any overrides from entries (custom permissions/titles) are applied
    const entriesMap = new Map(entries.map((e) => [(e.url || '').toLowerCase().trim().replace(/\/$/, ''), e]));
    const idMap = new Map(entries.map((e) => [e.id, e]));

    const mergedDailyList = dailySource.map((daily) => {
      const normUrl = (daily.url || '').toLowerCase().trim().replace(/\/$/, '');
      const matched = idMap.get(daily.id) || (normUrl ? entriesMap.get(normUrl) : null);
      if (matched) {
        return {
          ...matched,
          ...daily,
          name: daily.customDisplayName || daily.name || matched.customDisplayName || matched.name,
          team: daily.team || matched.team,
          accessEmails: (daily.accessEmails && daily.accessEmails.length > 0) ? daily.accessEmails : (matched.accessEmails || []),
        };
      }
      return daily;
    });

    return mergedDailyList.filter((entry) => {
      if (!entry) return false;
      const isPinned = pinnedSet.has(entry.id);

      // Check explicit user permissions for Daily Tasks
      if (!hasUserAccessToDailyTask(entry, activeUserEmail, userRoles, userAssignedTeams) && !isPinned) {
        return false;
      }

      // Role check
      const categories = Array.isArray(entry.categories) ? entry.categories : [];
      if (categories.length > 0) {
        if (!categories.some((c) => isCategoryAllowedForRoles(c, userRoles))) return false;
      }

      return true;
    });
  }, [entries, pinnedIds, activeUserEmail, userRoles, userAssignedTeams]);

  // 2. Team counts specifically for accessible Daily Tasks
  const col1TeamCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    allAccessibleDailyTasks.forEach((entry) => {
      if (entry.team) {
        entry.team
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean)
          .forEach((t) => {
            const key = t.toLowerCase();
            counts[key] = (counts[key] || 0) + 1;
          });
      }
    });
    return counts;
  }, [allAccessibleDailyTasks]);

  // 3. Team list for Column 1 dropdown
  const col1SortedTeamsList = useMemo(() => {
    const adminSettings = getLocalAdminSettings();
    const deletedTeams = adminSettings.deletedTeams || [];
    const deletedSet = new Set(deletedTeams.map((t) => t.toLowerCase().trim()));

    const userAssignedLowerSet = new Set(
      userAssignedTeams.map((t: string) => t.toLowerCase().trim())
    );

    const teamNamesSet = new Set<string>();
    allAccessibleDailyTasks.forEach((e) => {
      if (e && typeof e.team === 'string') {
        e.team
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean)
          .forEach((t) => teamNamesSet.add(t));
      }
    });
    userAssignedTeams.forEach((t: string) => {
      if (t) teamNamesSet.add(t.trim());
    });

    const validTeamNames = Array.from(teamNamesSet).filter((t) => {
      const lower = t.toLowerCase().trim();
      return (
        lower !== '' &&
        lower !== 'management' &&
        lower !== 'no team' &&
        lower !== 'unassigned' &&
        !deletedSet.has(lower)
      );
    });

    return validTeamNames
      .map((name) => {
        const count = col1TeamCounts[name.toLowerCase().trim()] || 0;
        const isAssigned = userAssignedLowerSet.has(name.toLowerCase().trim());
        return { name, count, isAssigned };
      })
      .sort((a, b) => {
        if (a.isAssigned && !b.isAssigned) return -1;
        if (!a.isAssigned && b.isAssigned) return 1;
        return a.name.localeCompare(b.name);
      });
  }, [allAccessibleDailyTasks, userAssignedTeams, col1TeamCounts]);

  // 4. Column 1 Filtered and Sorted entries
  const col1Entries = useMemo(() => {
    const pinnedSet = new Set(pinnedIds);

    const scoredList: Array<{ entry: DirectoryEntry; score: number }> = [];

    allAccessibleDailyTasks.forEach((entry) => {
      const isPinned = pinnedSet.has(entry.id);

      // Multi-team filter
      if (col1SelectedTeams.length > 0 && !col1SelectedTeams.includes('All')) {
        const teamStr = typeof entry.team === 'string' ? entry.team.toUpperCase() : '';
        const entryTeams = teamStr.split(',').map((t) => t.trim()).filter(Boolean);
        const matchesTeam = col1SelectedTeams.some((sel) => {
          const selUpper = sel.toUpperCase().trim();
          if (selUpper === 'SUPERADMIN') return teamStr.includes('ADMIN');
          return entryTeams.some((et) => et.includes(selUpper) || selUpper.includes(et));
        });
        if (!matchesTeam && !isPinned) return;
      }

      // Column 1 Search with tokenized matching, punctuation normalization & fuzzy tolerance
      let searchScore = 0;
      if (col1Search.trim()) {
        const match = evaluateSearchMatch(col1Search, {
          primary: getFormattedEntryTitle(entry),
          secondary: [
            entry.name,
            entry.description,
            ...((entry as any).activeTabs || []).map((t: any) => t.name)
          ],
          tertiary: [
            entry.team,
            ...(entry.categories || [])
          ]
        });
        if (!match.matched) return;
        searchScore = match.score;
      }

      scoredList.push({ entry, score: searchScore });
    });

    const activeTeam = col1SelectedTeams.length === 1 && col1SelectedTeams[0] !== 'All'
      ? col1SelectedTeams[0]
      : (userAssignedTeams.length === 1 ? userAssignedTeams[0] : null);

    const teamOrders = adminSettingsState.teamSheetOrder || {};
    let customOrderList: string[] | null = null;
    if (activeTeam) {
      if (teamOrders[activeTeam]) {
        customOrderList = teamOrders[activeTeam];
      } else {
        const matchKey = Object.keys(teamOrders).find(
          (k) => k.toLowerCase().trim() === activeTeam.toLowerCase().trim()
        );
        if (matchKey) customOrderList = teamOrders[matchKey];
      }
    }

    const orderIndexMap = new Map<string, number>();
    if (customOrderList && Array.isArray(customOrderList)) {
      customOrderList.forEach((idOrCode, idx) => {
        if (!idOrCode) return;
        orderIndexMap.set(idOrCode, idx);
        orderIndexMap.set(idOrCode.toLowerCase(), idx);
      });
    }

    const getEntryCustomOrderIdx = (entry: DirectoryEntry): number => {
      if (orderIndexMap.has(entry.id)) return orderIndexMap.get(entry.id)!;
      if (orderIndexMap.has(entry.id.toLowerCase())) return orderIndexMap.get(entry.id.toLowerCase())!;
      const normName = `name:${(entry.name || '').toLowerCase().trim()}`;
      if (orderIndexMap.has(normName)) return orderIndexMap.get(normName)!;
      return 999999;
    };

    // Sort: Pinned first, then search relevance score (if searching), then custom team order, then sequential workflow priority
    scoredList.sort((itemA, itemB) => {
      const a = itemA.entry;
      const b = itemB.entry;
      const aPinned = pinnedSet.has(a.id);
      const bPinned = pinnedSet.has(b.id);
      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;
      if (aPinned && bPinned) {
        return pinnedIds.indexOf(a.id) - pinnedIds.indexOf(b.id);
      }

      // If search query is active, rank higher search relevance scores first
      if (col1Search.trim() && itemA.score !== itemB.score) {
        return itemB.score - itemA.score;
      }

      // Check active team custom order
      const aOrder = getEntryCustomOrderIdx(a);
      const bOrder = getEntryCustomOrderIdx(b);
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }

      // If viewing multiple teams / all teams, check if entry's team has a defined order
      if (!activeTeam) {
        const getEntryTeamOrder = (entry: DirectoryEntry) => {
          if (!entry.team) return 999999;
          const entryTeams = entry.team.split(',').map((t) => t.trim());
          for (const t of entryTeams) {
            const matchKey = Object.keys(teamOrders).find(
              (k) => k.toLowerCase().trim() === t.toLowerCase().trim()
            );
            const list = matchKey ? teamOrders[matchKey] : undefined;
            if (list && Array.isArray(list)) {
              if (list.includes(entry.id)) return list.indexOf(entry.id);
              if (list.includes(entry.id.toLowerCase())) return list.indexOf(entry.id.toLowerCase());
              const nameIdx = list.indexOf(`name:${(entry.name || '').toLowerCase().trim()}`);
              if (nameIdx !== -1) return nameIdx;
            }
          }
          return 999999;
        };
        const oA = getEntryTeamOrder(a);
        const oB = getEntryTeamOrder(b);
        if (oA !== oB && (oA !== 999999 || oB !== 999999)) {
          return oA - oB;
        }
      }

      // Morning workflow sequencing heuristics
      const getWorkflowWeight = (e: DirectoryEntry) => {
        const title = (e.name || '').toLowerCase();
        if (title.includes('huddle') || title.includes('morning') || title.includes('checklist') || title.includes('daily recap')) return 1;
        if (title.includes('schedule') || title.includes('intake') || title.includes('front desk') || title.includes('reception')) return 2;
        if (title.includes('surgery') || title.includes('clinical') || title.includes('mohs') || title.includes('procedure')) return 3;
        if (title.includes('task') || title.includes('microtask') || title.includes('todo') || title.includes('log')) return 4;
        if (title.includes('operation') || title.includes('lab') || title.includes('billing')) return 5;
        return 6;
      };

      const weightDiff = getWorkflowWeight(a) - getWorkflowWeight(b);
      if (weightDiff !== 0) return weightDiff;
      return (a.name || '').localeCompare(b.name || '');
    });

    return scoredList.map((item) => item.entry);
  }, [allAccessibleDailyTasks, col1SelectedTeams, col1Search, pinnedIds, userAssignedTeams, adminSettingsState]);

  // =========================================================================
  // COLUMN 2: Printable Documents & Custom Groups
  // =========================================================================
  const printableDocGroups = useMemo(() => {
    const groups = adminSettingsState.printableDocGroups || [];
    const currentUserEmail = (auth.previewUserEmail || auth.user?.email || '').toLowerCase().trim();
    return groups.filter(g =>
      isGroupVisibleToUser(
        g,
        currentUserEmail,
        adminSettingsState.printableDocGroupVisibilityMode || 'everyone'
      )
    );
  }, [adminSettingsState.printableDocGroups, adminSettingsState.printableDocGroupVisibilityMode, auth.previewUserEmail, auth.user]);

  const allPrintableDocs: PrintableDoc[] = useMemo(() => {
    const raw = (printableDocsData as any[]).map((doc, idx) => ({
      id: doc.id || ('print-doc-' + (idx + 1)),
      docNumber: doc.docNumber || ('' + (idx + 1)),
      category: doc.category || 'General Forms',
      title: (doc.title || doc.fullName || 'Clinical Form').trim(),
      fullName: (doc.fullName || doc.title || 'Clinical Form').trim(),
      pdfUrl: doc.pdfUrl || '',
      docUrl: doc.docUrl || '',
      updateDate: doc.updateDate || '2025-02-18',
    }));

    // Defensive Deduplication: filter out junk header rows and ensure unique title & id
    const seenTitles = new Set<string>();
    const seenIds = new Set<string>();
    const deduped: PrintableDoc[] = [];

    for (const doc of raw) {
      if (!doc.title || doc.title.toLowerCase() === 'document #' || (!doc.pdfUrl && !doc.docUrl)) {
        continue;
      }
      const normTitle = doc.title.toLowerCase();
      if (seenTitles.has(normTitle) || seenIds.has(doc.id)) {
        continue;
      }
      seenTitles.add(normTitle);
      seenIds.add(doc.id);
      deduped.push(doc);
    }

    return deduped;
  }, []);

  const col2Docs = useMemo(() => {
    const pinnedSet = new Set(pinnedIds);

    let baseList: PrintableDoc[] = [];

    if (col2SelectedGroup && col2SelectedGroup !== 'All') {
      const selectedGroup = printableDocGroups.find(g => g.id === col2SelectedGroup);
      if (selectedGroup && Array.isArray(selectedGroup.documentIds)) {
        const docMap = new Map<string, PrintableDoc>();
        allPrintableDocs.forEach(d => {
          docMap.set(d.id, d);
        });
        baseList = selectedGroup.documentIds
          .map(id => {
            const resolvedId = LEGACY_DOC_ID_ALIASES[id] || id;
            return docMap.get(resolvedId) || docMap.get(id);
          })
          .filter((d): d is PrintableDoc => Boolean(d));
      } else {
        baseList = allPrintableDocs;
      }
    } else {
      baseList = allPrintableDocs;
    }

    const scoredList: Array<{ doc: PrintableDoc; score: number }> = [];

    baseList.forEach((doc) => {
      let searchScore = 0;
      if (col2Search.trim()) {
        const match = evaluateSearchMatch(col2Search, {
          primary: doc.title,
          secondary: [doc.fullName, doc.docNumber, doc.id],
          tertiary: doc.category
        });
        if (!match.matched) return;
        searchScore = match.score;
      }
      scoredList.push({ doc, score: searchScore });
    });

    scoredList.sort((itemA, itemB) => {
      const aPinned = pinnedSet.has(itemA.doc.id);
      const bPinned = pinnedSet.has(itemB.doc.id);
      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;

      if (col2Search.trim() && itemA.score !== itemB.score) {
        return itemB.score - itemA.score;
      }

      return 0;
    });

    return scoredList.map((item) => item.doc);
  }, [allPrintableDocs, col2Search, pinnedIds, col2SelectedGroup, printableDocGroups]);

  // Remove a document from the currently active group in Column 2
  const handleRemoveDocFromCurrentGroup = async (docId: string) => {
    if (!col2SelectedGroup || col2SelectedGroup === 'All') return;
    const group = printableDocGroups.find(g => g.id === col2SelectedGroup);
    if (!group) return;

    const currentDocIds = Array.isArray(group.documentIds) ? group.documentIds : [];
    const updatedDocIds = currentDocIds.filter(id => {
      const resolved = LEGACY_DOC_ID_ALIASES[id] || id;
      return resolved !== docId && id !== docId;
    });

    const updatedGroup: PrintableDocGroup = {
      ...group,
      documentIds: updatedDocIds,
      updatedAt: Date.now(),
    };

    try {
      await savePrintableDocGroup(updatedGroup);
      setAdminSettingsState(getLocalAdminSettings());
    } catch (err) {
      console.error('Failed to remove document from group:', err);
    }
  };

  // Helper to convert Google Drive / Docs links to embeddable preview URLs
  const getEmbedUrl = (url: string) => {
    if (!url) return '';
    if (url.includes('drive.google.com/file/d/')) {
      const match = url.match(/\/file\/d\/([^/]+)/);
      if (match && match[1]) {
        return 'https://drive.google.com/file/d/' + match[1] + '/preview';
      }
    }
    if (url.includes('docs.google.com/document/d/')) {
      const match = url.match(/\/document\/d\/([^/]+)/);
      if (match && match[1]) {
        return 'https://docs.google.com/document/d/' + match[1] + '/preview';
      }
    }
    return url;
  };
  // =========================================================================
  // COLUMN 3: All Sheets (Live Site Sheets by Team)
  // =========================================================================
  const col3Entries = useMemo(() => {
    const pinnedSet = new Set(pinnedIds);

    // 1. Filter entries accessible to user by permissions, roles, and selected team
    const accessibleEntries = entries.filter((entry) => {
      if (!entry) return false;
      const isPinned = pinnedSet.has(entry.id);

      // Check explicit user permissions for All Sheets / SuperAdmin
      if (!hasUserAccessToEntry(entry, activeUserEmail, userRoles, userAssignedTeams) && !isPinned) {
        return false;
      }

      // Role check
      const categories = Array.isArray(entry.categories) ? entry.categories : [];
      if (categories.length > 0) {
        if (!categories.some((c) => isCategoryAllowedForRoles(c, userRoles))) return false;
      }

      // Multi-team filter
      if (col3SelectedTeams.length > 0 && !col3SelectedTeams.includes('All')) {
        const teamStr = typeof entry.team === 'string' ? entry.team.toUpperCase() : '';
        const entryTeams = teamStr.split(',').map((t) => t.trim()).filter(Boolean);
        const matchesTeam = col3SelectedTeams.some((sel) => {
          const selUpper = sel.toUpperCase().trim();
          if (selUpper === 'SUPERADMIN') return teamStr.includes('ADMIN');
          return entryTeams.some((et) => et.includes(selUpper) || selUpper.includes(et));
        });
        if (!matchesTeam && !isPinned) return false;
      }

      return true;
    });

    let scoredList: Array<{ entry: DirectoryEntry; score: number }>;

    // If search is active, collapse results to the main tab per spreadsheet with sub-tabs attached
    if (col3Search.trim()) {
      scoredList = groupEntriesBySpreadsheetForSearch(accessibleEntries, col3Search, pinnedSet);
    } else {
      scoredList = accessibleEntries.map((entry) => ({ entry, score: 0 }));
    }

    const activeTeam = col3SelectedTeams.length === 1 && col3SelectedTeams[0] !== 'All'
      ? col3SelectedTeams[0]
      : (userAssignedTeams.length === 1 ? userAssignedTeams[0] : null);

    const teamOrders = adminSettingsState.teamSheetOrder || {};
    let customOrderList: string[] | null = null;
    if (activeTeam) {
      if (teamOrders[activeTeam]) {
        customOrderList = teamOrders[activeTeam];
      } else {
        const matchKey = Object.keys(teamOrders).find(
          (k) => k.toLowerCase().trim() === activeTeam.toLowerCase().trim()
        );
        if (matchKey) customOrderList = teamOrders[matchKey];
      }
    }

    const orderIndexMap = new Map<string, number>();
    if (customOrderList && Array.isArray(customOrderList)) {
      customOrderList.forEach((idOrCode, idx) => {
        if (!idOrCode) return;
        orderIndexMap.set(idOrCode, idx);
        orderIndexMap.set(idOrCode.toLowerCase(), idx);
      });
    }

    const getEntryCustomOrderIdx = (entry: DirectoryEntry): number => {
      if (orderIndexMap.has(entry.id)) return orderIndexMap.get(entry.id)!;
      if (orderIndexMap.has(entry.id.toLowerCase())) return orderIndexMap.get(entry.id.toLowerCase())!;
      const normName = `name:${(entry.name || '').toLowerCase().trim()}`;
      if (orderIndexMap.has(normName)) return orderIndexMap.get(normName)!;
      return 999999;
    };

    // Sort: Pinned first, then search relevance score (if searching), then custom team order, then alphabetically by title
    scoredList.sort((itemA, itemB) => {
      const a = itemA.entry;
      const b = itemB.entry;
      const aPinned = pinnedSet.has(a.id);
      const bPinned = pinnedSet.has(b.id);
      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;
      if (aPinned && bPinned) {
        return pinnedIds.indexOf(a.id) - pinnedIds.indexOf(b.id);
      }

      // If search query is active, rank higher search relevance scores first
      if (col3Search.trim() && itemA.score !== itemB.score) {
        return itemB.score - itemA.score;
      }

      // Check active team custom order
      const aOrder = getEntryCustomOrderIdx(a);
      const bOrder = getEntryCustomOrderIdx(b);
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }

      // If viewing multiple teams / all teams, check if entry's team has a defined order
      if (!activeTeam) {
        const getEntryTeamOrder = (entry: DirectoryEntry) => {
          if (!entry.team) return 999999;
          const entryTeams = entry.team.split(',').map((t) => t.trim());
          for (const t of entryTeams) {
            const matchKey = Object.keys(teamOrders).find(
              (k) => k.toLowerCase().trim() === t.toLowerCase().trim()
            );
            const list = matchKey ? teamOrders[matchKey] : undefined;
            if (list && Array.isArray(list)) {
              if (list.includes(entry.id)) return list.indexOf(entry.id);
              if (list.includes(entry.id.toLowerCase())) return list.indexOf(entry.id.toLowerCase());
              const nameIdx = list.indexOf(`name:${(entry.name || '').toLowerCase().trim()}`);
              if (nameIdx !== -1) return nameIdx;
            }
          }
          return 999999;
        };
        const oA = getEntryTeamOrder(a);
        const oB = getEntryTeamOrder(b);
        if (oA !== oB && (oA !== 999999 || oB !== 999999)) {
          return oA - oB;
        }
      }

      return (a.name || '').localeCompare(b.name || '');
    });

    return scoredList.map((item) => item.entry);
  }, [entries, col3SelectedTeams, col3Search, pinnedIds, activeUserEmail, userRoles, userAssignedTeams, adminSettingsState]);

  if (auth.isLoading) {
    return <SkeletonPage />;
  }

  if (!auth.isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
          <img src="/ssd-logo.png" alt="SSD Logo" className="mx-auto h-16 w-auto object-contain mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Southern Skies Dermatology</h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Intranet Portal & Workflow Dashboard</p>
          <div className="mt-6 flex justify-center">
            <div ref={googleButtonRef} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-950 flex flex-col text-gray-900 dark:text-gray-100">
      {/* SuperAdmin Active Preview Banner */}
      {auth.previewUserEmail && (
        <div className="bg-amber-600 text-white px-4 py-2 text-xs md:text-sm font-semibold flex items-center justify-between shadow-md z-40">
          <div className="flex items-center gap-2">
            <span>👁️ <strong>SuperAdmin Preview Mode:</strong> Viewing as <strong>{auth.previewUserEmail}</strong> ({userRoles.join(', ')})</span>
          </div>
          <button
            onClick={() => auth.setPreviewUserEmail(null)}
            className="bg-white text-amber-900 hover:bg-amber-50 px-2.5 py-1 rounded-md text-xs font-bold transition-colors shadow-sm"
          >
            ✕ Exit Preview
          </button>
        </div>
      )}

      {/* Main Header */}
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 shadow-sm px-4 lg:px-8 py-3.5 flex flex-wrap items-center justify-between gap-4 sticky top-0 z-30">
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center gap-3 group">
            <img src="/ssd-logo.png" alt="SSD Logo" className="h-10 w-auto object-contain group-hover:scale-105 transition-transform" />
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                  {branding?.title || 'Southern Skies Dermatology'}
                </h1>
                <span className="text-[10px] font-mono font-extrabold text-amber-500 bg-amber-50 dark:bg-amber-950/80 px-2 py-0.5 rounded border border-amber-400/40 shadow-sm">
                  {APP_VERSION}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                Unified Workflow Portal
              </p>
            </div>
          </Link>
        </div>

        {/* Right Header Controls */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* SuperAdmin Preview User Button */}
          {auth.user && auth.canPreview && (
            <button
              onClick={() => setShowPreviewModal(true)}
              className={'px-2.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors border shadow-sm flex-shrink-0 ' + (
                auth.previewUserEmail
                  ? 'bg-amber-600 text-white border-amber-500 hover:bg-amber-500'
                  : 'bg-gray-100 dark:bg-gray-800 text-amber-600 dark:text-amber-400 border-amber-400/50 hover:bg-gray-200 dark:hover:bg-gray-700'
              )}
              title="Preview how intranet looks for specific users or teams"
            >
              <span>👁️</span>
              <span className="hidden sm:inline">{auth.previewUserEmail ? ('Previewing: ' + auth.previewUserEmail.split('@')[0]) : 'Preview User Site'}</span>
            </button>
          )}

          {/* Support Requests Tracker Link */}
          <button
            onClick={() => setShowSupportTrackerModal(true)}
            className="px-2.5 py-1.5 text-xs font-semibold bg-gray-100 dark:bg-gray-800 hover:bg-amber-50 dark:hover:bg-amber-950/50 text-gray-700 dark:text-gray-200 hover:text-amber-700 dark:hover:text-amber-300 rounded-lg shadow-sm transition-colors flex items-center gap-1.5 border border-gray-200 dark:border-gray-700 hover:border-amber-400 cursor-pointer"
            title="Track support requests & status"
          >
            <span>✋</span>
            <span className="hidden sm:inline">Requests</span>
            {pendingSupportCount > 0 && (
              <span className="bg-amber-500 text-gray-950 text-[10px] font-bold px-1.5 py-0.2 rounded-full">
                {pendingSupportCount}
              </span>
            )}
          </button>

          {/* Bookmarks Page Link */}
          <Link
            to="/bookmarks"
            className="px-2.5 py-1.5 text-xs font-semibold bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg shadow-sm transition-colors flex items-center gap-1.5 border border-gray-200 dark:border-gray-700"
            title="View Bookmarked Sheets"
          >
            <span>🔖</span>
            <span className="hidden sm:inline">Bookmarks</span>
          </Link>

          {/* Admin Console Link */}
          {isAdminUser && (
            <Link
              to="/admin"
              className="px-2.5 py-1.5 text-xs font-bold bg-blue-600 hover:bg-blue-500 text-white rounded-lg shadow-sm transition-colors flex items-center gap-1.5 border border-blue-500/50"
              title="Admin Control Panel"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="hidden sm:inline">Admin</span>
            </Link>
          )}

          {/* Dark Mode Toggle */}
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 shadow-sm"
            aria-label="Toggle dark mode"
          >
            {darkMode ? (
              <svg className="w-4 h-4 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
              </svg>
            )}
          </button>

          {/* User Profile Info */}
          <div className="flex items-center gap-2.5 pl-2 border-l border-gray-200 dark:border-gray-800">
            <div className="text-right hidden sm:block">
              <div className="text-xs font-bold text-gray-900 dark:text-white truncate max-w-[150px]">
                {auth.user?.name || auth.user?.email}
              </div>
              <div className="text-[11px] text-amber-600 dark:text-amber-400 font-medium truncate max-w-[150px]">
                {userAssignedTeams.join(', ') || 'General Staff'}
              </div>
            </div>
            {auth.user?.picture ? (
              <img src={auth.user.picture} alt="" className="w-8 h-8 rounded-full ring-2 ring-amber-500/30" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-blue-600 text-white font-bold flex items-center justify-center text-xs shadow-sm">
                {(getUserDisplayName(auth.user) || auth.user?.email || 'U')[0].toUpperCase()}
              </div>
            )}
            <button
              onClick={() => auth.signOut()}
              className="text-xs bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-2.5 py-1.5 rounded-lg font-medium transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* Error alert if any */}
      {error && (
        <div className="max-w-[1800px] w-full mx-auto p-4">
          <div className="bg-red-50 dark:bg-red-950/40 border-l-4 border-red-500 p-3 rounded text-xs text-red-700 dark:text-red-300 flex items-center justify-between">
            <span>{error}</span>
            <button
              onClick={() => {
                clearCache();
                window.location.reload();
              }}
              className="underline font-bold hover:text-red-800"
            >
              Refresh Cache
            </button>
          </div>
        </div>
      )}

      {/* 3-Column Layout Container */}
      <main className="flex-1 p-4 lg:p-6 max-w-[1800px] w-full mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          
          {/* ================================================================= */}
          {/* COLUMN 1: Daily Tasks */}
          {/* ================================================================= */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-md border border-gray-200 dark:border-gray-800 flex flex-col h-[calc(100vh-140px)] min-h-[700px] overflow-hidden">
            {/* Column Header */}
            <div className="p-4 bg-gradient-to-r from-emerald-50/80 to-teal-50/80 dark:from-emerald-950/30 dark:to-teal-950/30 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-emerald-600 text-white flex items-center justify-center font-bold shadow-sm">
                    📋
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-gray-900 dark:text-white leading-tight">
                      Daily Tasks
                    </h2>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isAdminUser && (
                    <button
                      onClick={() => {
                        if (col1Reordering) {
                          setCol1Reordering(false);
                        } else {
                          if (col1SelectedTeams.length === 1 && col1SelectedTeams[0] !== 'All') {
                            setCol1Reordering(true);
                          } else if (userAssignedTeams.length === 1) {
                            setCol1SelectedTeams([userAssignedTeams[0]]);
                            setCol1Reordering(true);
                          } else {
                            setTeamPickerCol(1);
                          }
                        }
                      }}
                      className={`text-xs font-semibold px-2.5 py-1 rounded-lg border transition-all flex items-center gap-1.5 shadow-sm ${
                        col1Reordering
                          ? 'bg-emerald-600 text-white border-emerald-500 shadow-emerald-500/20'
                          : 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900'
                      }`}
                      title="Rearrange sheet order for a team"
                    >
                      <span>{col1Reordering ? '✓ Done' : '⇅ Rearrange'}</span>
                    </button>
                  )}
                  <span className="bg-emerald-100 dark:bg-emerald-900/60 text-emerald-700 dark:text-emerald-300 text-xs font-bold px-2.5 py-0.5 rounded-full">
                    {col1Entries.length} Tasks
                  </span>
                </div>
              </div>

              {/* Rearrange Mode Info Banner (Admin only) */}
              {isAdminUser && col1Reordering && (
                <div className="mt-2.5 p-2.5 rounded-xl bg-emerald-100/70 dark:bg-emerald-950/70 border border-emerald-300 dark:border-emerald-700 flex items-center justify-between gap-2 text-xs">
                  <div className="flex items-center gap-2 text-emerald-900 dark:text-emerald-100 min-w-0">
                    <span className="text-base">↕️</span>
                    <div className="truncate">
                      <span className="font-bold">
                        Rearranging for {col1SelectedTeams.length === 1 && col1SelectedTeams[0] !== 'All' ? col1SelectedTeams[0] : (userAssignedTeams[0] || 'Team')}
                      </span>
                      <span className="text-[11px] text-emerald-700 dark:text-emerald-300 block sm:inline sm:ml-1.5">
                        • Drag ⠿ or click ▲ / ▼ to set order
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => handleResetTeamOrder(col1SelectedTeams.length === 1 && col1SelectedTeams[0] !== 'All' ? col1SelectedTeams[0] : (userAssignedTeams[0] || ''))}
                      className="text-[11px] font-medium text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 px-2 py-0.5 rounded hover:bg-white/60 dark:hover:bg-gray-800 transition-colors"
                      title="Reset order to default A-Z"
                    >
                      ↺ Reset A-Z
                    </button>
                    <button
                      onClick={() => setCol1Reordering(false)}
                      className="text-xs font-bold bg-emerald-600 hover:bg-emerald-500 text-white px-2.5 py-1 rounded-lg shadow-sm transition-colors"
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}

              {/* Space-Saving Multi-Select Team Dropdown & Search Input */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                {/* Team Dropdown */}
                <div>
                  <MultiSelectTeamDropdown
                    selectedTeams={col1SelectedTeams}
                    onChange={setCol1SelectedTeams}
                    teams={col1SortedTeamsList}
                    userAssignedTeams={userAssignedTeams}
                    totalSheetsCount={allAccessibleDailyTasks.length}
                    colorScheme="emerald"
                  />
                </div>

                {/* Search Input */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={col1Search}
                    onChange={(e) => setCol1Search(e.target.value)}
                    className="w-full text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg pl-2.5 pr-7 py-1.5 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:outline-none shadow-sm"
                  />
                  {col1Search && (
                    <button
                      onClick={() => setCol1Search('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xs font-bold"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Column 1 Scrollable Tasks List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2.5 divide-y divide-gray-100 dark:divide-gray-800/60">
              {loading ? (
                <div className="py-12 text-center text-xs text-gray-400 animate-pulse">
                  Loading tasks...
                </div>
              ) : col1Entries.length === 0 ? (
                <div className="py-12 text-center text-xs text-gray-400">
                  No tasks found matching selected team filter.
                </div>
              ) : (
                col1Entries.map((entry, index) => {
                  const isPinned = pinnedIds.includes(entry.id);
                  const activeTabs = (entry as any).activeTabs || [];
                  const activeTeamName = col1SelectedTeams.length === 1 && col1SelectedTeams[0] !== 'All'
                    ? col1SelectedTeams[0]
                    : (userAssignedTeams[0] || '');
                  const isReordering = isAdminUser && col1Reordering;
                  const isDragging = isReordering && draggedId === entry.id;
                  const isOver = isReordering && dragOverId === entry.id;

                  return (
                    <div
                      key={entry.id || index}
                      draggable={isReordering}
                      onDragStart={(e) => {
                        if (!isReordering) return;
                        setDraggedId(entry.id);
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                      onDragOver={(e) => {
                        if (!isReordering) return;
                        e.preventDefault();
                        setDragOverId(entry.id);
                        e.dataTransfer.dropEffect = 'move';
                      }}
                      onDragLeave={() => {
                        if (dragOverId === entry.id) setDragOverId(null);
                      }}
                      onDrop={(e) => {
                        if (!isReordering) return;
                        e.preventDefault();
                        if (draggedId) {
                          handleDropSheet(1, activeTeamName, draggedId, entry.id);
                        }
                      }}
                      className={`pt-2.5 first:pt-0 rounded-xl transition-all p-2 ${
                        isDragging
                          ? 'opacity-40 bg-emerald-100/50 dark:bg-emerald-950/40 border border-dashed border-emerald-500'
                          : isOver
                          ? 'bg-emerald-50 dark:bg-emerald-950/30 border-t-2 border-emerald-500'
                          : 'hover:bg-gray-50/80 dark:hover:bg-gray-800/40'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {isReordering && (
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <span
                                className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 p-0.5 text-base select-none"
                                title="Drag to reorder"
                              >
                                ⠿
                              </span>
                              <span className="text-[10px] font-bold bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded border border-emerald-200 dark:border-emerald-800 min-w-[24px] text-center">
                                #{index + 1}
                              </span>
                            </div>
                          )}

                          <button
                            onClick={(e) => togglePin(entry.id, e)}
                            title={isPinned ? 'Unpin sheet' : 'Pin to top of list'}
                            className={'p-1 rounded-lg text-sm transition-transform hover:scale-110 flex-shrink-0 hover:bg-gray-100 dark:hover:bg-gray-800 ' + (
                              isPinned
                                ? 'text-amber-500 dark:text-amber-400'
                                : 'text-gray-300 dark:text-gray-600 hover:text-amber-500 dark:hover:text-amber-400'
                            )}
                          >
                            <svg className="w-4 h-4 transform -rotate-45" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
                            </svg>
                          </button>

                          <div className="flex-1 min-w-0">
                            <a
                              href={entry.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-usage-handled="true"
                              onClick={() => {
                                logFileOpen({
                                  fileId: entry.id,
                                  fileName: getFormattedEntryTitle(entry),
                                  fileUrl: entry.url,
                                  source: 'Daily Tasks',
                                  action: 'open_link',
                                  category: entry.categories ? entry.categories.join(', ') : 'Daily Tasks',
                                  team: entry.team || (col1SelectedTeams[0] !== 'All' ? col1SelectedTeams[0] : ''),
                                  userEmail: auth.user?.email,
                                  userName: auth.user?.name,
                                });
                              }}
                              className="text-xs font-bold text-gray-900 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors truncate block"
                              title="Open Google Sheet"
                            >
                              {getFormattedEntryTitle(entry)}
                            </a>

                            {/* Active Tab Direct Links */}
                            {activeTabs.length > 0 && (
                              <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                                {activeTabs.slice(0, 4).map((tab: { name: string; url: string }) => (
                                  <a
                                    key={tab.name}
                                    href={tab.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-usage-handled="true"
                                    onClick={() => {
                                      logFileOpen({
                                        fileId: `${entry.id}-${tab.name}`,
                                        fileName: `${getFormattedEntryTitle(entry)} - ${tab.name}`,
                                        fileUrl: tab.url,
                                        source: 'Daily Tasks',
                                        action: 'open_tab',
                                        category: entry.categories ? entry.categories.join(', ') : 'Daily Tasks',
                                        team: entry.team || (col1SelectedTeams[0] !== 'All' ? col1SelectedTeams[0] : ''),
                                        userEmail: auth.user?.email,
                                        userName: auth.user?.name,
                                      });
                                    }}
                                    className="text-[10px] bg-white dark:bg-gray-800 hover:bg-emerald-50 dark:hover:bg-emerald-950 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 rounded border border-gray-200 dark:border-gray-700 hover:border-emerald-400 transition-colors truncate max-w-[150px]"
                                  >
                                    ↳ {tab.name}
                                  </a>
                                ))}
                                {activeTabs.length > 4 && (
                                  <span className="text-[10px] text-gray-400">
                                    +{activeTabs.length - 4} more
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {/* Raise Hand Support Request Button */}
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedFileForSupport({
                                fileId: entry.id,
                                fileName: getFormattedEntryTitle(entry),
                                fileUrl: entry.url,
                                categoryOrTeam: activeTeamName || (entry as any).team || 'Daily Tasks',
                                sourceColumn: 1,
                                columnLabel: 'Daily Tasks',
                              });
                            }}
                            title="Raise Hand / Request Support for this sheet"
                            className="p-1 rounded-lg text-sm hover:bg-amber-100 dark:hover:bg-amber-950/60 text-amber-600 dark:text-amber-400 hover:scale-110 active:scale-95 transition-all cursor-pointer flex items-center justify-center flex-shrink-0"
                          >
                            <span className="text-sm sm:text-base leading-none">✋</span>
                          </button>

                          {/* Info Hover Tooltip (Complete Sheet Name) */}
                          <FileInfoTooltip
                            completeName={entry.description?.trim() || entry.name?.trim() || getFormattedEntryTitle(entry)}
                          />

                          {isReordering && (
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMoveSheet(1, activeTeamName, index, 'up');
                                }}
                                disabled={index === 0}
                                title="Move Up"
                                className="p-1 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 hover:text-emerald-700 dark:hover:text-emerald-300 disabled:opacity-25 disabled:cursor-not-allowed text-xs transition-colors"
                              >
                                ▲
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMoveSheet(1, activeTeamName, index, 'down');
                                }}
                                disabled={index === col1Entries.length - 1}
                                title="Move Down"
                                className="p-1 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 hover:text-emerald-700 dark:hover:text-emerald-300 disabled:opacity-25 disabled:cursor-not-allowed text-xs transition-colors"
                              >
                                ▼
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* ================================================================= */}
          {/* COLUMN 2: Printable Documents */}
          {/* ================================================================= */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-md border border-gray-200 dark:border-gray-800 flex flex-col h-[calc(100vh-140px)] min-h-[700px] overflow-hidden">
            {/* Column Header */}
            <div className="p-4 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 dark:from-blue-950/30 dark:to-indigo-950/30 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold shadow-sm">
                    🖨️
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-gray-900 dark:text-white leading-tight">
                      Printable Documents
                    </h2>
                  </div>
                </div>
                <span className="bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300 text-xs font-bold px-2.5 py-0.5 rounded-full">
                  {col2Docs.length} Docs
                </span>
              </div>

              {/* Document Group Selector Dropdown & New Group Button */}
              <div className="mt-2.5 flex items-center gap-1.5">
                <div className="relative flex-1">
                  <select
                    value={col2SelectedGroup}
                    onChange={(e) => setCol2SelectedGroup(e.target.value)}
                    className="w-full text-xs font-semibold bg-white dark:bg-gray-800 border border-blue-300 dark:border-blue-750 rounded-lg px-2.5 py-1.5 text-blue-900 dark:text-blue-200 focus:ring-2 focus:ring-blue-500 focus:outline-none shadow-sm cursor-pointer appearance-none pr-7 truncate"
                  >
                    <option value="All">📁 All Printable Documents ({allPrintableDocs.length})</option>
                    {printableDocGroups.map((g) => (
                      <option key={g.id} value={g.id}>
                        📑 {g.name} ({(g.documentIds || []).length})
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-blue-600 dark:text-blue-400 text-xs">
                    ▼
                  </div>
                </div>
                <button
                  onClick={() => {
                    setDocForGroupModal(null);
                    setEditingGroupIdForModal(null);
                    setShowGroupModal(true);
                  }}
                  className="text-[11px] font-bold text-emerald-700 hover:text-white dark:text-emerald-300 dark:hover:text-white px-2 py-1.5 bg-emerald-50 hover:bg-emerald-600 dark:bg-emerald-950/50 dark:hover:bg-emerald-600 rounded-lg border border-emerald-200 dark:border-emerald-750 transition-all flex-shrink-0 cursor-pointer flex items-center gap-1 shadow-sm"
                  title="Create New Group"
                >
                  <span className="font-bold">+</span>
                  <span className="hidden sm:inline">Group</span>
                </button>
                {col2SelectedGroup !== 'All' && (
                  <button
                    onClick={() => {
                      setDocForGroupModal(null);
                      setEditingGroupIdForModal(col2SelectedGroup);
                      setShowGroupModal(true);
                    }}
                    className="text-[11px] font-bold text-blue-700 hover:text-white dark:text-blue-300 dark:hover:text-white px-2 py-1.5 bg-blue-50 hover:bg-blue-600 dark:bg-blue-950/50 dark:hover:bg-blue-600 rounded-lg border border-blue-200 dark:border-blue-750 transition-all flex-shrink-0 cursor-pointer flex items-center gap-1 shadow-sm"
                    title="Edit Selected Group"
                  >
                    <span>✏️</span>
                    <span className="hidden sm:inline">Edit</span>
                  </button>
                )}
                {col2SelectedGroup !== 'All' && (
                  <button
                    onClick={() => setCol2SelectedGroup('All')}
                    className="text-[11px] font-bold text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-300 px-2 py-1.5 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 transition-colors flex-shrink-0 cursor-pointer"
                    title="View All Printable Documents"
                  >
                    Show All
                  </button>
                )}
              </div>

              {/* Search */}
              <div className="mt-2">
                <input
                  type="text"
                  placeholder={col2SelectedGroup === 'All' ? `Search all ${allPrintableDocs.length} printable forms...` : 'Search within selected group...'}
                  value={col2Search}
                  onChange={(e) => setCol2Search(e.target.value)}
                  className="w-full text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-2.5 py-1.5 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:outline-none shadow-sm"
                />
              </div>
            </div>

            {/* Column 2 Scrollable Documents List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
              {col2Docs.length === 0 ? (
                <div className="py-12 text-center text-xs text-gray-400 space-y-2">
                  <p className="font-semibold text-gray-500 dark:text-gray-400">
                    {col2Search.trim() ? 'No printable forms found matching your search.' : 'No documents assigned to this group yet.'}
                  </p>
                  {col2SelectedGroup !== 'All' && !col2Search.trim() && (
                    <button
                      onClick={() => setCol2SelectedGroup('All')}
                      className="text-xs text-blue-600 dark:text-blue-400 underline font-bold cursor-pointer"
                    >
                      View All Printable Documents
                    </button>
                  )}
                </div>
              ) : (
                col2Docs.map((doc) => {
                  const isPinned = pinnedIds.includes(doc.id);

                  return (
                    <div
                      key={doc.id}
                      className="p-2.5 bg-white dark:bg-gray-800/80 rounded-xl border border-gray-200 dark:border-gray-700/80 hover:border-blue-400/80 dark:hover:border-blue-500/60 shadow-sm transition-all group"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <button
                            onClick={(e) => togglePin(doc.id, e)}
                            title={isPinned ? 'Unpin document' : 'Pin to top of list'}
                            className={'p-1 rounded-lg text-sm transition-transform hover:scale-110 flex-shrink-0 hover:bg-gray-100 dark:hover:bg-gray-800 ' + (
                              isPinned
                                ? 'text-amber-500 dark:text-amber-400'
                                : 'text-gray-300 dark:text-gray-600 hover:text-amber-500 dark:hover:text-amber-400'
                            )}
                          >
                            <svg className="w-4 h-4 transform -rotate-45" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
                            </svg>
                          </button>

                          <a
                            href={doc.docUrl || doc.pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-usage-handled="true"
                            onClick={() => {
                              logFileOpen({
                                fileId: doc.id,
                                fileName: doc.title,
                                fileUrl: doc.docUrl || doc.pdfUrl,
                                source: 'Printable Documents',
                                action: doc.docUrl ? 'open_doc' : 'download_pdf',
                                category: doc.category || 'Printable Documents',
                                team: col2SelectedGroup !== 'All' ? col2SelectedGroup : '',
                                userEmail: auth.user?.email,
                                userName: auth.user?.name,
                              });
                            }}
                            className="text-xs font-bold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 leading-tight truncate transition-colors block"
                            title="Open Document"
                          >
                            {doc.title}
                          </a>
                        </div>

                        {/* Document Action Buttons */}
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {/* Remove from Group (Only visible when viewing a specific group) */}
                          {col2SelectedGroup !== 'All' && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleRemoveDocFromCurrentGroup(doc.id);
                              }}
                              title="Remove document from this group"
                              className="p-1 sm:p-1.5 rounded-lg text-sm bg-rose-50 hover:bg-rose-600 text-rose-600 hover:text-white dark:bg-rose-950/40 dark:hover:bg-rose-600 dark:text-rose-400 dark:hover:text-white transition-all shadow-sm flex items-center justify-center cursor-pointer hover:scale-110 active:scale-95 flex-shrink-0"
                            >
                              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}

                          {/* Add to Group (+) Button */}
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setDocForGroupModal(doc);
                              setShowGroupModal(true);
                            }}
                            title="Add to group"
                            className="p-1 sm:p-1.5 rounded-lg text-sm bg-emerald-50 hover:bg-emerald-600 text-emerald-700 hover:text-white dark:bg-emerald-950/40 dark:hover:bg-emerald-600 dark:text-emerald-300 dark:hover:text-white transition-all shadow-sm flex items-center justify-center cursor-pointer hover:scale-110 active:scale-95"
                          >
                            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                            </svg>
                          </button>

                          {/* Preview Document Button (Only Eye Icon) */}
                          {(doc.pdfUrl || doc.docUrl) && (
                            <button
                              onClick={() => {
                                logFileOpen({
                                  fileId: doc.id,
                                  fileName: doc.title,
                                  fileUrl: doc.pdfUrl || doc.docUrl,
                                  source: 'Printable Documents',
                                  action: 'preview_pdf',
                                  category: doc.category || 'Printable Documents',
                                  team: col2SelectedGroup !== 'All' ? col2SelectedGroup : '',
                                  userEmail: auth.user?.email,
                                  userName: auth.user?.name,
                                });
                                setPreviewDoc(doc);
                              }}
                              className="p-1 sm:p-1.5 rounded-lg text-sm bg-blue-50 hover:bg-blue-600 text-blue-700 hover:text-white dark:bg-blue-950/40 dark:hover:bg-blue-600 dark:text-blue-300 dark:hover:text-white transition-all shadow-sm flex items-center justify-center cursor-pointer hover:scale-110 active:scale-95"
                              title="Preview Document"
                            >
                              <span className="text-sm sm:text-base leading-none">👁️</span>
                            </button>
                          )}

                          {/* Raise Hand Support Request Button */}
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedFileForSupport({
                                fileId: doc.id,
                                fileName: doc.title,
                                fileUrl: doc.docUrl || doc.pdfUrl,
                                categoryOrTeam: doc.category || 'Printable Documents',
                                sourceColumn: 2,
                                columnLabel: 'Printable Documents',
                              });
                            }}
                            title="Raise Hand / Request Support for this printable form"
                            className="p-1 rounded-lg text-sm hover:bg-amber-100 dark:hover:bg-amber-950/60 text-amber-600 dark:text-amber-400 hover:scale-110 active:scale-95 transition-all cursor-pointer flex items-center justify-center flex-shrink-0"
                          >
                            <span className="text-sm sm:text-base leading-none">✋</span>
                          </button>

                          {/* Info Hover Tooltip (Complete Document Name) */}
                          <FileInfoTooltip
                            completeName={doc.fullName?.trim() || doc.title?.trim()}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* ================================================================= */}
          {/* COLUMN 3: All Sheets */}
          {/* ================================================================= */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-md border border-gray-200 dark:border-gray-800 flex flex-col h-[calc(100vh-140px)] min-h-[700px] overflow-hidden">
            {/* Column Header */}
            <div className="p-4 bg-gradient-to-r from-purple-50/80 to-indigo-50/80 dark:from-purple-950/30 dark:to-indigo-950/30 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-purple-600 text-white flex items-center justify-center font-bold shadow-sm text-sm">
                    📑
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-gray-900 dark:text-white leading-tight">
                      All Sheets
                    </h2>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isAdminUser && (
                    <button
                      onClick={() => {
                        if (col3Reordering) {
                          setCol3Reordering(false);
                        } else {
                          if (col3SelectedTeams.length === 1 && col3SelectedTeams[0] !== 'All') {
                            setCol3Reordering(true);
                          } else if (userAssignedTeams.length === 1) {
                            setCol3SelectedTeams([userAssignedTeams[0]]);
                            setCol3Reordering(true);
                          } else {
                            setTeamPickerCol(3);
                          }
                        }
                      }}
                      className={`text-xs font-semibold px-2.5 py-1 rounded-lg border transition-all flex items-center gap-1.5 shadow-sm ${
                        col3Reordering
                          ? 'bg-purple-600 text-white border-purple-500 shadow-purple-500/20'
                          : 'bg-purple-50 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-900'
                      }`}
                      title="Rearrange sheet order for a team"
                    >
                      <span>{col3Reordering ? '✓ Done' : '⇅ Rearrange'}</span>
                    </button>
                  )}
                  <span className="bg-purple-100 dark:bg-purple-900/60 text-purple-700 dark:text-purple-300 text-xs font-bold px-2.5 py-0.5 rounded-full">
                    {col3Entries.length} Sheets
                  </span>
                </div>
              </div>

              {/* Rearrange Mode Info Banner (Admin only) */}
              {isAdminUser && col3Reordering && (
                <div className="mt-2.5 p-2.5 rounded-xl bg-purple-100/70 dark:bg-purple-950/70 border border-purple-300 dark:border-purple-700 flex items-center justify-between gap-2 text-xs">
                  <div className="flex items-center gap-2 text-purple-900 dark:text-purple-100 min-w-0">
                    <span className="text-base">↕️</span>
                    <div className="truncate">
                      <span className="font-bold">
                        Rearranging for {col3SelectedTeams.length === 1 && col3SelectedTeams[0] !== 'All' ? col3SelectedTeams[0] : (userAssignedTeams[0] || 'Team')}
                      </span>
                      <span className="text-[11px] text-purple-700 dark:text-purple-300 block sm:inline sm:ml-1.5">
                        • Drag ⠿ or click ▲ / ▼ to set order
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => handleResetTeamOrder(col3SelectedTeams.length === 1 && col3SelectedTeams[0] !== 'All' ? col3SelectedTeams[0] : (userAssignedTeams[0] || ''))}
                      className="text-[11px] font-medium text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 px-2 py-0.5 rounded hover:bg-white/60 dark:hover:bg-gray-800 transition-colors"
                      title="Reset order to default A-Z"
                    >
                      ↺ Reset A-Z
                    </button>
                    <button
                      onClick={() => setCol3Reordering(false)}
                      className="text-xs font-bold bg-purple-600 hover:bg-purple-500 text-white px-2.5 py-1 rounded-lg shadow-sm transition-colors"
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}

              {/* Space-Saving Multi-Select Team Dropdown & Search Input */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                {/* Team Dropdown */}
                <div>
                  <MultiSelectTeamDropdown
                    selectedTeams={col3SelectedTeams}
                    onChange={setCol3SelectedTeams}
                    teams={sortedTeamsList}
                    userAssignedTeams={userAssignedTeams}
                    totalSheetsCount={totalAccessibleCol3SheetsCount}
                    colorScheme="purple"
                  />
                </div>

                {/* Search Input */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={col3Search}
                    onChange={(e) => setCol3Search(e.target.value)}
                    className="w-full text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg pl-2.5 pr-7 py-1.5 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:outline-none shadow-sm"
                  />
                  {col3Search && (
                    <button
                      onClick={() => setCol3Search('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xs font-bold"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Column 3 Scrollable Sheets List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2.5 divide-y divide-gray-100 dark:divide-gray-800/60">
              {loading ? (
                <div className="py-12 text-center text-xs text-gray-400 animate-pulse">
                  Loading sheets...
                </div>
              ) : col3Entries.length === 0 ? (
                <div className="py-12 text-center text-xs text-gray-400">
                  No sheets found matching selected filter.
                </div>
              ) : (
                col3Entries.map((entry, index) => {
                  const isPinned = pinnedIds.includes(entry.id);
                  const activeTabs = (entry as any).activeTabs || [];
                  const activeTeamName = col3SelectedTeams.length === 1 && col3SelectedTeams[0] !== 'All'
                    ? col3SelectedTeams[0]
                    : (userAssignedTeams[0] || '');
                  const isReordering = isAdminUser && col3Reordering;
                  const isDragging = isReordering && draggedId === entry.id;
                  const isOver = isReordering && dragOverId === entry.id;

                  return (
                    <div
                      key={entry.id || index}
                      draggable={isReordering}
                      onDragStart={(e) => {
                        if (!isReordering) return;
                        setDraggedId(entry.id);
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                      onDragOver={(e) => {
                        if (!isReordering) return;
                        e.preventDefault();
                        setDragOverId(entry.id);
                        e.dataTransfer.dropEffect = 'move';
                      }}
                      onDragLeave={() => {
                        if (dragOverId === entry.id) setDragOverId(null);
                      }}
                      onDrop={(e) => {
                        if (!isReordering) return;
                        e.preventDefault();
                        if (draggedId) {
                          handleDropSheet(3, activeTeamName, draggedId, entry.id);
                        }
                      }}
                      className={`pt-2.5 first:pt-0 rounded-xl transition-all p-2 ${
                        isDragging
                          ? 'opacity-40 bg-purple-100/50 dark:bg-purple-950/40 border border-dashed border-purple-500'
                          : isOver
                          ? 'bg-purple-50 dark:bg-purple-950/30 border-t-2 border-purple-500'
                          : 'hover:bg-gray-50/80 dark:hover:bg-gray-800/40'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {isReordering && (
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <span
                                className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 p-0.5 text-base select-none"
                                title="Drag to reorder"
                              >
                                ⠿
                              </span>
                              <span className="text-[10px] font-bold bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded border border-purple-200 dark:border-purple-800 min-w-[24px] text-center">
                                #{index + 1}
                              </span>
                            </div>
                          )}

                          <button
                            onClick={(e) => togglePin(entry.id, e)}
                            title={isPinned ? 'Unpin sheet' : 'Pin to top of list'}
                            className={'p-1 rounded-lg text-sm transition-transform hover:scale-110 flex-shrink-0 hover:bg-gray-100 dark:hover:bg-gray-800 ' + (
                              isPinned
                                ? 'text-amber-500 dark:text-amber-400'
                                : 'text-gray-300 dark:text-gray-600 hover:text-amber-500 dark:hover:text-amber-400'
                            )}
                          >
                            <svg className="w-4 h-4 transform -rotate-45" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
                            </svg>
                          </button>

                          <div className="flex-1 min-w-0">
                            <a
                              href={entry.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-usage-handled="true"
                              onClick={() => {
                                logFileOpen({
                                  fileId: entry.id,
                                  fileName: getFormattedEntryTitle(entry),
                                  fileUrl: entry.url,
                                  source: 'All Sheets',
                                  action: 'open_link',
                                  category: entry.categories ? entry.categories.join(', ') : 'All Sheets',
                                  team: entry.team || (col3SelectedTeams[0] !== 'All' ? col3SelectedTeams[0] : ''),
                                  userEmail: auth.user?.email,
                                  userName: auth.user?.name,
                                });
                              }}
                              className="text-xs font-bold text-gray-900 dark:text-white hover:text-purple-600 dark:hover:text-purple-400 transition-colors truncate block"
                              title="Open Google Sheet"
                            >
                              {getFormattedEntryTitle(entry)}
                            </a>

                            {/* Direct Sub-Tab Clickable Links */}
                            {activeTabs.length > 0 && (
                              <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                                {activeTabs.slice(0, 4).map((tab: { name: string; url: string }) => (
                                  <a
                                    key={tab.name}
                                    href={tab.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-usage-handled="true"
                                    onClick={() => {
                                      logFileOpen({
                                        fileId: `${entry.id}-${tab.name}`,
                                        fileName: `${getFormattedEntryTitle(entry)} - ${tab.name}`,
                                        fileUrl: tab.url,
                                        source: 'All Sheets',
                                        action: 'open_tab',
                                        category: entry.categories ? entry.categories.join(', ') : 'All Sheets',
                                        team: entry.team || (col3SelectedTeams[0] !== 'All' ? col3SelectedTeams[0] : ''),
                                        userEmail: auth.user?.email,
                                        userName: auth.user?.name,
                                      });
                                    }}
                                    className="text-[10px] bg-white dark:bg-gray-800 hover:bg-purple-50 dark:hover:bg-purple-950 text-purple-600 dark:text-purple-400 px-2 py-0.5 rounded border border-gray-200 dark:border-gray-700 hover:border-purple-400 transition-colors truncate max-w-[150px]"
                                  >
                                    ↳ {tab.name}
                                  </a>
                                ))}
                                {activeTabs.length > 4 && (
                                  <span
                                    className="text-[10px] text-gray-400 cursor-help"
                                    title={activeTabs.slice(4).map((t: { name: string }) => t.name).join(', ')}
                                  >
                                    +{activeTabs.length - 4} more
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {/* Raise Hand Support Request Button */}
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedFileForSupport({
                                fileId: entry.id,
                                fileName: getFormattedEntryTitle(entry),
                                fileUrl: entry.url,
                                categoryOrTeam: activeTeamName || (entry as any).team || 'All Sheets',
                                sourceColumn: 3,
                                columnLabel: 'All Sheets',
                              });
                            }}
                            title="Raise Hand / Request Support for this sheet"
                            className="p-1 rounded-lg text-sm hover:bg-amber-100 dark:hover:bg-amber-950/60 text-amber-600 dark:text-amber-400 hover:scale-110 active:scale-95 transition-all cursor-pointer flex items-center justify-center flex-shrink-0"
                          >
                            <span className="text-sm sm:text-base leading-none">✋</span>
                          </button>

                          {/* Info Hover Tooltip (Complete Sheet Name) */}
                          <FileInfoTooltip
                            completeName={entry.description?.trim() || entry.name?.trim() || getFormattedEntryTitle(entry)}
                          />

                          {isReordering && (
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMoveSheet(3, activeTeamName, index, 'up');
                                }}
                                disabled={index === 0}
                                title="Move Up"
                                className="p-1 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-purple-100 dark:hover:bg-purple-900/60 hover:text-purple-700 dark:hover:text-purple-300 disabled:opacity-25 disabled:cursor-not-allowed text-xs transition-colors"
                              >
                                ▲
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMoveSheet(3, activeTeamName, index, 'down');
                                }}
                                disabled={index === col3Entries.length - 1}
                                title="Move Down"
                                className="p-1 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-purple-100 dark:hover:bg-purple-900/60 hover:text-purple-700 dark:hover:text-purple-300 disabled:opacity-25 disabled:cursor-not-allowed text-xs transition-colors"
                              >
                                ▼
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 py-4 px-4 text-center text-xs text-gray-500 dark:text-gray-400 mt-auto">
        <div className="flex flex-wrap justify-center gap-4 mb-2 font-medium">
          <Link to="/contact" className="hover:text-blue-600 transition-colors">Contact Support</Link>
          <span>•</span>
          <Link to="/sms-optin" className="hover:text-blue-600 transition-colors">SMS Notifications</Link>
          <span>•</span>
          <Link to="/terms" className="hover:text-blue-600 transition-colors">Terms of Service</Link>
          <span>•</span>
          <Link to="/privacy" className="hover:text-blue-600 transition-colors">Privacy Policy</Link>
        </div>
        <p>© {new Date().getFullYear()} Southern Skies Dermatology. All rights reserved. • {APP_VERSION} ({APP_BUILD_DATE})</p>
      </footer>

      {/* ================================================================= */}
      {/* MODAL: In-App Document Viewer Preview Modal */}
      {/* ================================================================= */}
      {previewDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="p-4 bg-gray-50 dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3 flex-shrink-0">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300">
                    Form #{previewDoc.docNumber}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {previewDoc.category}
                  </span>
                </div>
                <h3 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white truncate mt-0.5">
                  {previewDoc.title}
                </h3>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {previewDoc.pdfUrl && (
                  <a
                    href={previewDoc.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-usage-handled="true"
                    onClick={() => {
                      logFileOpen({
                        fileId: previewDoc.id,
                        fileName: previewDoc.title,
                        fileUrl: previewDoc.pdfUrl,
                        source: 'Printable Documents',
                        action: 'download_pdf',
                        category: previewDoc.category || 'Printable Documents',
                        userEmail: auth.user?.email,
                        userName: auth.user?.name,
                      });
                    }}
                    className="text-xs bg-blue-600 hover:bg-blue-500 text-white font-bold px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1 shadow-sm"
                  >
                    <span>↗</span> Open PDF
                  </a>
                )}
                {previewDoc.docUrl && (
                  <a
                    href={previewDoc.docUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-usage-handled="true"
                    onClick={() => {
                      logFileOpen({
                        fileId: previewDoc.id,
                        fileName: previewDoc.title,
                        fileUrl: previewDoc.docUrl,
                        source: 'Printable Documents',
                        action: 'open_doc',
                        category: previewDoc.category || 'Printable Documents',
                        userEmail: auth.user?.email,
                        userName: auth.user?.name,
                      });
                    }}
                    className="text-xs bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-bold px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1"
                  >
                    <span>📄</span> Doc
                  </a>
                )}
                <button
                  onClick={() => setPreviewDoc(null)}
                  className="w-8 h-8 rounded-lg bg-gray-200 hover:bg-red-100 hover:text-red-700 dark:bg-gray-700 dark:hover:bg-red-900/60 dark:hover:text-red-300 text-gray-700 dark:text-gray-200 font-bold flex items-center justify-center transition-colors text-base"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Modal Iframe Body */}
            <div className="flex-1 bg-gray-100 dark:bg-gray-950 p-1 relative">
              <iframe
                src={getEmbedUrl(previewDoc.pdfUrl || previewDoc.docUrl)}
                title={previewDoc.title}
                className="w-full h-full rounded-lg border-0 bg-white"
                allow="autoplay"
              />
            </div>
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* MODAL: SuperAdmin Role & Team Preview Switcher */}
      {/* ================================================================= */}
      {showPreviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="bg-gray-800 rounded-2xl shadow-2xl border border-gray-700 w-full max-w-lg p-5 flex flex-col gap-4 text-white">
            <div className="flex items-center justify-between border-b border-gray-700 pb-3">
              <div className="flex items-center gap-2">
                <span className="text-xl">👁️</span>
                <h3 className="text-base font-bold text-white">SuperAdmin Preview Switcher</h3>
              </div>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="text-gray-400 hover:text-white text-lg font-bold"
              >
                ✕
              </button>
            </div>

            <p className="text-xs text-gray-300">
              Preview the intranet exactly as any employee or department team sees it.
            </p>

            {/* Tabs */}
            <div className="flex border-b border-gray-700">
              <button
                type="button"
                onClick={() => setPreviewModalTab('user')}
                className={'flex-1 py-2 text-xs font-bold transition-colors border-b-2 ' + (
                  previewModalTab === 'user'
                    ? 'border-amber-500 text-amber-400 bg-gray-700/50'
                    : 'border-transparent text-gray-400 hover:text-gray-200'
                )}
              >
                👤 Preview by Employee
              </button>
              <button
                type="button"
                onClick={() => setPreviewModalTab('team')}
                className={'flex-1 py-2 text-xs font-bold transition-colors border-b-2 ' + (
                  previewModalTab === 'team'
                    ? 'border-amber-500 text-amber-400 bg-gray-700/50'
                    : 'border-transparent text-gray-400 hover:text-gray-200'
                )}
              >
                👥 Preview by Department Team
              </button>
            </div>

            {/* Tab 1: Employee Preview */}
            {previewModalTab === 'user' && (
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="Search employees..."
                  value={previewUserSearch}
                  onChange={(e) => setPreviewUserSearch(e.target.value)}
                  className="w-full text-xs bg-gray-900 border border-gray-700 rounded-lg px-2.5 py-1.5 text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                />

                <div className="max-h-52 overflow-y-auto space-y-1.5 pr-1">
                  {allSheetUserProfiles
                    .filter((u) => {
                      if (!previewUserSearch.trim()) return true;
                      return matchesSearch(previewUserSearch, {
                        primary: [u.name, u.email],
                        secondary: u.team
                      });
                    })
                    .map((user) => {
                      const isSelected = selectedPreviewEmail === user.email;
                      const displayName = user.name || getUserDisplayName({ email: user.email });
                      return (
                        <button
                          key={user.email}
                          type="button"
                          onClick={() => setSelectedPreviewEmail(user.email)}
                          className={'w-full text-left p-2.5 rounded-xl border transition-all flex items-center justify-between ' + (
                            isSelected
                              ? 'bg-amber-600/20 border-amber-500 text-white font-semibold shadow'
                              : 'bg-gray-900/80 border-gray-700/80 text-gray-300 hover:border-gray-600'
                          )}
                        >
                          <div className="min-w-0 pr-2">
                            <p className="text-xs font-bold text-gray-100 truncate">{displayName}</p>
                            <p className="font-mono text-[11px] text-gray-400 truncate">{user.email}</p>
                            <p className="text-[11px] text-gray-400 mt-0.5">
                              Team: <span className="text-amber-300 font-semibold">{user.team || 'General Staff'}</span>
                            </p>
                          </div>
                        </button>
                      );
                    })}
                </div>

                <div className="pt-2 border-t border-gray-700">
                  <label className="text-xs text-gray-400 mb-1 block">Or type employee email:</label>
                  <EmailComboboxInput
                    value={customPreviewEmail}
                    onChange={(val) => {
                      setCustomPreviewEmail(val);
                      if (val.trim()) {
                        setSelectedPreviewEmail(val.trim().toLowerCase());
                      }
                    }}
                    placeholder="Search name/email or click ▾ to select..."
                    userProfiles={allSheetUserProfiles}
                  />
                </div>
              </div>
            )}

            {/* Tab 2: Department Teams */}
            {previewModalTab === 'team' && (
              <div className="space-y-3">
                <label className="text-xs font-semibold text-gray-300">Select Department View:</label>
                <div className="grid grid-cols-2 gap-2 max-h-52 overflow-y-auto pr-1">
                  {allAvailableTeamsForModal.map((team) => {
                    const isSelected = selectedPreviewTeam === team;
                    return (
                      <button
                        key={team}
                        type="button"
                        onClick={() => setSelectedPreviewTeam(team)}
                        className={'p-2.5 rounded-xl border text-left text-xs font-semibold transition-all ' + (
                          isSelected
                            ? 'bg-amber-600 border-amber-500 text-white shadow-md font-bold'
                            : 'bg-gray-900 border-gray-700 text-gray-300 hover:border-gray-600'
                        )}
                      >
                        {team}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="pt-4 border-t border-gray-700 space-y-2">
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const targetEmail = previewModalTab === 'user'
                      ? selectedPreviewEmail
                      : 'team_' + selectedPreviewTeam.toLowerCase().replace(/[^a-z0-9]/g, '') + '@ssdspc.com';
                    window.open('/?previewUser=' + encodeURIComponent(targetEmail), '_blank');
                    setShowPreviewModal(false);
                  }}
                  className="flex-1 py-2.5 bg-amber-600 hover:bg-amber-500 text-white font-bold text-xs rounded-xl transition-colors shadow-lg flex items-center justify-center gap-1.5"
                >
                  <span>🚀</span> Open in New Window
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const targetEmail = previewModalTab === 'user'
                      ? selectedPreviewEmail
                      : 'team_' + selectedPreviewTeam.toLowerCase().replace(/[^a-z0-9]/g, '') + '@ssdspc.com';
                    auth.setPreviewUserEmail(targetEmail);
                    setShowPreviewModal(false);
                  }}
                  className="flex-1 py-2.5 bg-gray-700 hover:bg-gray-600 text-amber-300 font-bold text-xs rounded-xl transition-colors flex items-center justify-center gap-1.5"
                >
                  <span>👁️</span> Preview in Current Window
                </button>
              </div>
              {auth.previewUserEmail && (
                <button
                  type="button"
                  onClick={() => {
                    auth.setPreviewUserEmail(null);
                    setShowPreviewModal(false);
                  }}
                  className="w-full py-1.5 bg-red-950/60 hover:bg-red-900/80 text-red-300 font-semibold text-xs rounded-xl border border-red-800/50 transition-colors"
                >
                  Reset to Normal View
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* MODAL: Select Team to Rearrange Sheets (SuperAdmin / Admin only) */}
      {/* ================================================================= */}
      {isAdminUser && teamPickerCol !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-md p-5 flex flex-col gap-4">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-3">
              <div className="flex items-center gap-2">
                <span className="text-xl">↕️</span>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                  Select Team to Rearrange
                </h3>
              </div>
              <button
                onClick={() => setTeamPickerCol(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-white text-lg font-bold"
              >
                ✕
              </button>
            </div>

            <p className="text-xs text-gray-600 dark:text-gray-300">
              Pick the specific team whose sheet order you want to customize. All members of this team will see the sheets in this exact order:
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-60 overflow-y-auto pr-1">
              {sortedTeamsList.map((team) => (
                <button
                  key={team.name}
                  type="button"
                  onClick={() => {
                    if (teamPickerCol === 1) {
                      setCol1SelectedTeams([team.name]);
                      setCol1Reordering(true);
                    } else if (teamPickerCol === 3) {
                      setCol3SelectedTeams([team.name]);
                      setCol3Reordering(true);
                    }
                    setTeamPickerCol(null);
                  }}
                  className="p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-emerald-500 dark:hover:border-purple-500 bg-gray-50 dark:bg-gray-900 hover:bg-emerald-50/50 dark:hover:bg-purple-950/40 text-left text-xs font-semibold text-gray-900 dark:text-white transition-all shadow-sm flex items-center justify-between"
                >
                  <span className="font-bold">👥 {team.name}</span>
                  <span className="text-[10px] text-gray-400 bg-gray-200 dark:bg-gray-800 px-1.5 py-0.5 rounded-full font-medium">
                    {team.count}
                  </span>
                </button>
              ))}
            </div>

            <div className="pt-2 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setTeamPickerCol(null)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 text-xs font-bold rounded-xl transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Reorder Saved Notification Toast */}
      {reorderSavedNotice && (
        <div className="fixed bottom-6 right-6 z-50 bg-gray-900 text-white dark:bg-emerald-600 px-4 py-3 rounded-2xl shadow-2xl border border-gray-700 dark:border-emerald-500 flex items-center gap-2.5 text-xs font-bold animate-in slide-in-from-bottom-3 duration-200">
          <span>✨</span>
          <span>{reorderSavedNotice}</span>
        </div>
      )}

      {/* ================================================================= */}
      {/* MODAL: Raise Hand / Request Support Modal */}
      {/* ================================================================= */}
      <RaiseHandModal
        isOpen={selectedFileForSupport !== null}
        onClose={() => setSelectedFileForSupport(null)}
        targetFile={selectedFileForSupport}
        currentUser={auth.user}
        onRequestCreated={() => {
          setAllSupportRequests(getLocalSupportRequests());
        }}
      />

      {/* ================================================================= */}
      {/* MODAL: Support Requests Tracker Modal */}
      {/* ================================================================= */}
      <SupportRequestsTrackerModal
        isOpen={showSupportTrackerModal}
        onClose={() => setShowSupportTrackerModal(false)}
        currentUser={auth.user}
        isAdminUser={isAdminUser}
      />

      {/* ================================================================= */}
      {/* MODAL: Add Document to Group & Create Group */}
      {/* ================================================================= */}
      <AddToGroupModal
        isOpen={showGroupModal}
        onClose={() => {
          setShowGroupModal(false);
          setDocForGroupModal(null);
          setEditingGroupIdForModal(null);
        }}
        document={docForGroupModal}
        groups={printableDocGroups}
        onSaveGroup={async (group) => {
          await savePrintableDocGroup(group);
          setAdminSettingsState(getLocalAdminSettings());
        }}
        onDeleteGroup={async (groupId) => {
          await deletePrintableDocGroup(groupId);
          if (col2SelectedGroup === groupId) {
            setCol2SelectedGroup('All');
          }
          setAdminSettingsState(getLocalAdminSettings());
        }}
        currentUserEmail={auth.previewUserEmail || auth.user?.email}
        currentUserName={auth.user?.name}
        initialEditingGroupId={editingGroupIdForModal}
      />
    </div>
  );
}
